import Chip from "@mui/material/Chip";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Form from "react-bootstrap/Form";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";

import { AllderTable } from "../../../../components/AllderTable";
import { useGlobalTable } from "../../../../hooks/useGlobalTable";
import { memberHandler } from "../../../../redux/handlers";
import { createTableColumns } from "../../../../utils/createTableColumns";

export default function DebtorRecordTab() {
  const { businessSlug, customerID } = useParams();
  const dispatch = useDispatch();

  const [searchdata, setSearchdata] = useState({
    startDate: moment().subtract(1, "month").format("DD/MM/YYYY"),
    endDate: moment().format("DD/MM/YYYY"),
  });

  const fetchDebtorPaymentData = useCallback(
    async (params) => {
      const startDate = moment(searchdata.startDate, "DD/MM/YYYY").format(
        "YYYY-MM-DD"
      );
      const endDate = moment(searchdata.endDate, "DD/MM/YYYY").format(
        "YYYY-MM-DD"
      );

      const queryParams = {
        memberId: customerID,
        startDate,
        endDate,
        perPage: params.per_page,
        ...params,
      };

      return dispatch(
        memberHandler.getMemberHistoryDebtorPaymentPagination(
          businessSlug,
          queryParams
        )
      );
    },
    [
      dispatch,
      customerID,
      businessSlug,
      searchdata.startDate,
      searchdata.endDate,
    ]
  );

  const tableDependencies = useMemo(
    () => [searchdata.startDate, searchdata.endDate],
    [searchdata.startDate, searchdata.endDate]
  );

  const {
    data: debtData,
    loading: debtLoading,
    totalRows,
    paginationModel,
    setPaginationModel,
    handleSort,
    debouncedSearch,
    filterParams,
  } = useGlobalTable({
    fetchData: fetchDebtorPaymentData,
    defaultSort: "created_at",
    defaultOrder: "desc",
    defaultPageSize: 10,
    dependencies: tableDependencies,
  });

  const handleExport = useCallback(() => {
    const startDate = moment(searchdata.startDate, "DD/MM/YYYY").format(
      "YYYY-MM-DD"
    );
    const endDate = moment(searchdata.endDate, "DD/MM/YYYY").format(
      "YYYY-MM-DD"
    );

    const params = {
      memberId: customerID,
      start_date: startDate,
      end_date: endDate,
      s: filterParams.s,
    };

    dispatch(
      memberHandler.getMemberHistoryDebtorPaymentExcel(businessSlug, params)
    );
  }, [
    dispatch,
    customerID,
    businessSlug,
    searchdata.startDate,
    searchdata.endDate,
    filterParams.s,
  ]);

  const handleApply = useCallback((event, picker) => {
    const newStartDate = picker.startDate.format("DD/MM/YYYY");
    const newEndDate = picker.endDate.format("DD/MM/YYYY");

    setSearchdata((prev) => ({
      ...prev,
      startDate: newStartDate,
      endDate: newEndDate,
    }));
  }, []);

  const formatCurrency = useCallback((amount) => {
    if (!amount && amount !== 0) return "0.00";
    return new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  const getFullName = useCallback((userBusinessGroup) => {
    if (!userBusinessGroup) return "-";
    const firstName = userBusinessGroup.first_name || "";
    const lastName = userBusinessGroup.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || "-";
  }, []);

  const debtColumns = useMemo(
    () =>
      createTableColumns([
        {
          field: "date",
          headerName: "วันที่",
          flex: 0.8,
          sortable: false,
          renderCell: ({ row }) => moment(row.created_at).format("DD/MM/YYYY"),
        },
        {
          field: "branchName",
          headerName: "สาขา",
          flex: 1,
          sortable: false,
          renderCell: ({ row }) =>
            row.order?.branchPosSelling?.branchPos?.branch?.branch_name || "-",
        },
        {
          field: "receipt_number",
          headerName: "เลขที่ใบเสร็จ",
          flex: 1.2,
          sortable: false,
          renderCell: ({ row }) => (
            <Link
              to={`/${businessSlug}/report/bill/detail/${row.id}`}
              style={{
                color: "#565DFF",
                fontWeight: 500,
                textDecoration: "underline",
              }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.receipt_no}
            </Link>
          ),
        },
        {
          field: "net_total",
          headerName: "ยอดสุทธิ",
          flex: 0.9,
          sortable: false,
          renderCell: ({ row }) => formatCurrency(row.final_amount),
        },
        {
          field: "product",
          headerName: "จำนวนรายการ",
          flex: 0.8,
          sortable: false,
          renderCell: ({ row }) => row.billItems?.length || "-",
        },
        {
          field: "paymentChannels",
          headerName: "ช่องทางชำระ",
          flex: 1,
          sortable: false,
          renderCell: ({ row }) => {
            const debtPayment = row.payment?.find(
              (p) => p.paymentMethod?.code === "ACCOUNT_RECEIVABLE"
            );
            return debtPayment?.paymentMethod?.name || "-";
          },
        },
        {
          field: "debt",
          headerName: "ยอดบันทึกลูกหนี้",
          flex: 1,
          sortable: false,
          renderCell: ({ row }) => {
            const debtPayment = row.payment?.find(
              (p) => p.paymentMethod?.code === "ACCOUNT_RECEIVABLE"
            );
            const debtAmount = debtPayment?.price || 0;
            return (
              <div className="text-[#FF0000]">{formatCurrency(debtAmount)}</div>
            );
          },
        },
        {
          field: "status",
          headerName: "สถานะ",
          flex: 0.9,
          sortable: false,
          renderCell: ({ row }) => {
            const hasDebt = true; // TODO: Implement actual debt status logic
            return (
              <div>
                {hasDebt ? (
                  <Chip
                    label="ค้างชำระ"
                    className="!text-[#F24236] !bg-[#FFECEC]"
                  />
                ) : (
                  <Chip
                    label="ชำระแล้ว"
                    className="!text-[#72E128] !bg-[#EEFBE5]"
                  />
                )}
              </div>
            );
          },
        },
        {
          field: "salesperson",
          headerName: "พนักงานขาย",
          flex: 1.3,
          sortable: false,
          renderCell: ({ row }) => getFullName(row.order?.userBusinessGroup),
        },
      ]),
    [businessSlug, formatCurrency, getFullName]
  );

  return (
    <div className="card card-st-2">
      <div className="card-body px-0 pt-4 pb-0">
        <div className="text-lg font-bold mb-4 lh-1 px-4 text-[#767676]">
          รายการบันทึกลูกหนี้
        </div>

        <div className="d-flex justify-content-between align-items-center px-4 mb-3 flex-wrap">
          <div className="d-flex align-items-center mb-2">
            <button className="btn btn-export-st-1" onClick={handleExport}>
              Export
            </button>
          </div>

          <div className="d-flex align-items-center mb-2">
            <div className="w-[300px] ml-auto">
              <Form.Group className="mb-3 form-st-3 pb-1">
                <Form.Label className="mb-1">เลือกช่วงวันที่</Form.Label>
                <DateRangePicker
                  initialSettings={{
                    startDate: searchdata.startDate,
                    endDate: searchdata.endDate,
                    locale: {
                      format: "DD/MM/YYYY",
                    },
                  }}
                  onApply={handleApply}
                >
                  <input
                    type="text"
                    className="form-control datepicker2 h-[44px]"
                    value={`${searchdata.startDate} - ${searchdata.endDate}`}
                    readOnly
                  />
                </DateRangePicker>
              </Form.Group>
            </div>

            <div className="mx-4 mb-2">
              <input
                type="text"
                className="form-control form-st-4"
                placeholder="ค้นหาเลขที่ใบเสร็จ"
                onKeyUp={(e) => debouncedSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <AllderTable
          data={debtData}
          loading={debtLoading}
          columns={debtColumns}
          totalRows={totalRows}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          handleSort={handleSort}
        />
      </div>
    </div>
  );
}
